import { PolarAngleAxis, PolarGrid, Radar, RadarChart } from 'recharts'
import { HugeiconsIcon } from '@hugeicons/react'
import { Analytics01Icon } from '@hugeicons/core-free-icons'

import { ErrorDisplay } from '@/components/common/error-display'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ChartContainer, ChartTooltip } from '@/components/ui/chart'
import { Skeleton } from '@/components/ui/skeleton'

export interface RadarDataPoint {
  category: string
  manager: number
  self: number
}

export interface PositionRadarChartProps {
  data: Array<RadarDataPoint>
  isLoading: boolean
  error?: Error | null
}

const chartConfig = {
  manager: {
    label: 'Manager',
    color: 'var(--chart-1)',
  },
  self: {
    label: 'Self',
    color: 'var(--chart-2)',
  },
}

export function PositionRadarChart({
  data,
  isLoading,
  error,
}: PositionRadarChartProps) {
  if (isLoading) {
    return (
      <Card className="h-[400px] flex flex-col p-6 bg-muted/30 rounded-lg border-none shadow-none">
        <CardHeader className="p-0 mb-4">
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent className="flex-1 p-0">
          <Skeleton className="h-full w-full" />
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <ErrorDisplay
        error={error}
        title="Failed to load evaluation data"
        variant="card"
      />
    )
  }

  if (data.length === 0) {
    return (
      <Card className="h-[400px] flex flex-col p-6 bg-muted/30 rounded-lg border-none shadow-none">
        <CardHeader className="p-0 mb-4">
          <CardTitle className="text-lg font-semibold">
            Current Evaluation
          </CardTitle>
        </CardHeader>
        <CardContent className="flex-1 p-0">
          <div className="flex h-full items-center justify-center flex-col gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <HugeiconsIcon
                icon={Analytics01Icon}
                className="h-6 w-6 text-primary"
              />
            </div>
            <span className="text-muted-foreground">
              No evaluation data available
            </span>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="h-[400px] flex flex-col p-6 bg-muted/30 rounded-lg border-none shadow-none">
      <CardHeader className="p-0 mb-4">
        <CardTitle className="text-lg font-semibold">
          Current Evaluation
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-1 p-0">
        <ChartContainer config={chartConfig} className="h-full w-full">
          <RadarChart data={data}>
            <ChartTooltip
              cursor={false}
              contentStyle={{
                backgroundColor: 'var(--background)',
                borderColor: 'var(--border)',
                color: 'var(--foreground)',
              }}
              formatter={(value) => {
                const n = typeof value === 'number' ? value : Number(value)
                return Number.isFinite(n) ? n.toFixed(2) : '0.00'
              }}
              labelFormatter={(label) => `${label}`}
            />
            <PolarAngleAxis
              dataKey="category"
              tick={{ fontSize: 16, fill: 'var(--foreground)' }}
            />
            <PolarGrid />
            <Radar
              animationDuration={1000}
              dataKey="manager"
              name="Manager"
              fill="var(--color-manager)"
              fillOpacity={0.6}
              stroke="var(--color-manager)"
            />
            <Radar
              animationDuration={1000}
              dataKey="self"
              name="Self"
              fill="var(--color-self)"
              fillOpacity={0.6}
              stroke="var(--color-self)"
            />
          </RadarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}
